import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Select, { components } from "react-select";
import Swal from "sweetalert2";

import CashVendorServices from "../../../services/CashVendorServices";

const readData = (response) => {
  const body = response?.data?.status
    ? response.data
    : response;

  return body?.data;
};

const errorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Unable to process vendor details.";

// Supports quoted CSV values, commas and newlines inside quotes.
const parseVendorCsv = (text) => {
  const records = [];
  let record = [];
  let value = "";
  let quoted = false;

  const source = text.replace(/^\uFEFF/, "");

  for (let index = 0; index < source.length; index++) {
    const character = source[index];

    if (character === '"') {
      if (quoted && source[index + 1] === '"') {
        value += '"';
        index++;
      } else if (quoted || value === "") {
        quoted = !quoted;
      } else {
        throw new Error("Invalid CSV quoting.");
      }
    } else if (character === "," && !quoted) {
      record.push(value.trim());
      value = "";
    } else if (
      (character === "\n" || character === "\r") &&
      !quoted
    ) {
      if (character === "\r" && source[index + 1] === "\n") {
        index++;
      }

      record.push(value.trim());

      if (record.some(Boolean)) records.push(record);

      record = [];
      value = "";
    } else {
      value += character;
    }
  }

  if (quoted) {
    throw new Error("The CSV contains an unclosed quoted value.");
  }

  record.push(value.trim());
  if (record.some(Boolean)) records.push(record);

  if (records.length < 2) {
    throw new Error("The CSV must contain a header and vendor rows.");
  }

  const headers = records[0].map((header) =>
    header.toLowerCase().replace(/\s+/g, "")
  );

  const nameIndex = headers.indexOf("name");
  const mobileIndex = headers.indexOf("mobileno");

  if (nameIndex === -1) {
    throw new Error('The CSV must contain a "name" column.');
  }

  const vendors = records.slice(1).map((row, index) => {
    const name = row[nameIndex]?.trim() || "";

    if (!name) {
      throw new Error(`CSV row ${index + 2}: Vendor name is required.`);
    }

    return {
      name,
      mobileNo:
        mobileIndex === -1
          ? ""
          : row[mobileIndex]?.trim() || ""
    };
  });

  if (vendors.length > 1000) {
    throw new Error("Please import a maximum of 1000 vendors.");
  }

  return vendors;
};

const VendorMenuList = (props) => (
  <components.MenuList {...props}>
    {props.children}

    <div className="cash-vendor-menu-actions">
      <button
        type="button"
        disabled={props.selectProps.vendorBusy}
        onMouseDown={(event) => event.preventDefault()}
        onClick={props.selectProps.onAddVendor}
      >
        <i className="fa fa-plus me-2" aria-hidden="true" />
        Add New Vendor
      </button>

      <button
        type="button"
        disabled={props.selectProps.vendorBusy}
        onMouseDown={(event) => event.preventDefault()}
        onClick={props.selectProps.onImportVendors}
      >
        <i className="fa fa-upload me-2" aria-hidden="true" />
        Import Vendors from CSV
      </button>
    </div>
  </components.MenuList>
);

const CashOutVendorField = ({
  rowId,
  value,
  onChange,
  disabled = false
}) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [name, setName] = useState("");
  const [mobileNo, setMobileNo] = useState("");

  const csvInputRef = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const loadVendors = async () => {
      setLoading(true);

      try {
        const response =
          await CashVendorServices.getCashVendors("active");

        const data = readData(response);

        if (!Array.isArray(data?.vendors)) {
          throw new Error("Unexpected vendor API response.");
        }

        if (!cancelled) setVendors(data.vendors);
      } catch (error) {
        if (!cancelled) {
          Swal.fire("Error", errorMessage(error), "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadVendors();

    return () => {
      cancelled = true;
    };
  }, []);

  const mergeVendors = (newVendors) => {
    setVendors((previous) => {
      const vendorMap = new Map(
        previous.map((vendor) => [String(vendor._id), vendor])
      );

      newVendors.forEach((vendor) => {
        vendorMap.set(String(vendor._id), vendor);
      });

      return [...vendorMap.values()].sort((first, second) =>
        first.name.localeCompare(second.name)
      );
    });
  };

  const openAddModal = () => {
    if (disabled || busyRef.current) return;

    setMenuOpen(false);
    setName("");
    setMobileNo("");
    setShowModal(true);
  };

  const browseCsv = () => {
    if (disabled || busyRef.current) return;

    setMenuOpen(false);
    csvInputRef.current?.click();
  };

  const saveVendor = async (event) => {
    event.preventDefault();

    if (disabled || busyRef.current) return;

    if (!name.trim()) {
      await Swal.fire(
        "Required",
        "Please enter a vendor name.",
        "warning"
      );
      return;
    }

    busyRef.current = true;
    setBusy(true);

    try {
      const response = await CashVendorServices.createCashVendor({
        name: name.trim(),
        mobileNo: mobileNo.trim()
      });

      const vendor = readData(response)?.vendor;

      if (!vendor?._id) {
        throw new Error("Unexpected vendor creation response.");
      }

      mergeVendors([vendor]);
      onChange(String(vendor._id));

      await Swal.fire(
        "Success",
        "Vendor created successfully.",
        "success"
      );

      setShowModal(false);
    } catch (error) {
      await Swal.fire("Error", errorMessage(error), "error");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const importCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || disabled || busyRef.current) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      await Swal.fire(
        "Invalid File",
        "Please select a CSV file.",
        "warning"
      );
      return;
    }

    busyRef.current = true;
    setBusy(true);

    try {
      const csvRows = parseVendorCsv(await file.text());

      const response =
        await CashVendorServices.importCashVendors(csvRows);

      const data = readData(response);

      if (!Array.isArray(data?.vendors)) {
        throw new Error("Unexpected vendor import response.");
      }

      mergeVendors(data.vendors);

      await Swal.fire(
        "Import Complete",
        `${data.imported ?? data.vendors.length} vendors imported. ${
          data.skipped ?? 0
        } duplicate rows skipped.`,
        "success"
      );
    } catch (error) {
      await Swal.fire("Error", errorMessage(error), "error");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const options = vendors.map((vendor) => ({
    value: String(vendor._id),
    label: vendor.name
  }));

  return (
    <>
      <Select
        inputId={`cash-out-vendor-${rowId}`}
        options={options}
        value={
          options.find((option) => option.value === value) || null
        }
        onChange={(selected) =>
          onChange(selected?.value || "")
        }
        placeholder={loading ? "Loading Vendors..." : "Search Vendor"}
        isSearchable
        isClearable
        isLoading={loading || busy}
        isDisabled={disabled || loading || busy}
        menuIsOpen={menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        noOptionsMessage={() => "No vendor found"}
        components={{ MenuList: VendorMenuList }}
        onAddVendor={openAddModal}
        onImportVendors={browseCsv}
        vendorBusy={busy}
        className="erp-select"
        classNamePrefix="erp-select"
        menuPlacement="auto"
      />

      <input
        ref={csvInputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        disabled={disabled || busy}
        onChange={importCsv}
      />

      <Modal
        show={showModal}
        onHide={() => !busy && setShowModal(false)}
        centered
        backdrop="static"
        keyboard={!busy}
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Add New Vendor</Modal.Title>
        </Modal.Header>

        <form onSubmit={saveVendor}>
          <Modal.Body className="erp-compact">
            <div className="mb-3">
              <label
                htmlFor={`vendor-name-${rowId}`}
                className="form-label"
              >
                Vendor Name <span className="text-danger">*</span>
              </label>

              <input
                id={`vendor-name-${rowId}`}
                type="text"
                className="form-control"
                placeholder="Enter vendor name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={busy}
              />
            </div>

            <div>
              <label
                htmlFor={`vendor-mobile-${rowId}`}
                className="form-label"
              >
                Mobile Number (Optional)
              </label>

              <input
                id={`vendor-mobile-${rowId}`}
                type="tel"
                className="form-control"
                placeholder="Enter mobile number"
                value={mobileNo}
                onChange={(event) => setMobileNo(event.target.value)}
                disabled={busy}
              />
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="btn btn-danger erp-small-button"
              onClick={() => setShowModal(false)}
              disabled={busy}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="btn btn-primary erp-small-button"
              disabled={busy || !name.trim()}
            >
              {busy ? "Saving..." : "Save"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

export default CashOutVendorField;